import { useEffect, useState, useCallback } from 'react'

const STORAGE_KEY = 'gps-journal-project-time'
const CHANGE_EVENT = 'gps-project-time-changed'

interface ProjectTimeRecord {
  date: string // YYYY-MM-DD
  minutesLogged: number
  workedOn: string
}

function todayKey(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function loadRaw(): ProjectTimeRecord | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as ProjectTimeRecord
  } catch {
    return null
  }
}

function saveRaw(record: ProjectTimeRecord) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record))
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT))
  } catch {
    // ignore
  }
}

/**
 * Shared, reactive store for today's project time logged.
 * Resets at midnight (when the date key changes).
 */
export function useProjectTime() {
  const initial = (() => {
    const record = loadRaw()
    if (record && record.date === todayKey()) return record
    return { date: todayKey(), minutesLogged: 0, workedOn: '' }
  })()

  const [minutesLogged, setMinutesLoggedState] = useState(initial.minutesLogged)
  const [workedOn, setWorkedOnState] = useState(initial.workedOn)

  // Listen for cross-component / cross-tab updates
  useEffect(() => {
    const refresh = () => {
      const record = loadRaw()
      if (record && record.date === todayKey()) {
        setMinutesLoggedState(record.minutesLogged)
        setWorkedOnState(record.workedOn)
      } else {
        setMinutesLoggedState(0)
        setWorkedOnState('')
      }
    }
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) refresh()
    }
    window.addEventListener(CHANGE_EVENT, refresh)
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener(CHANGE_EVENT, refresh)
      window.removeEventListener('storage', onStorage)
    }
  }, [])

  const addMinutes = useCallback(
    (delta: number) => {
      setMinutesLoggedState((prev) => {
        const next = Math.max(0, prev + delta)
        saveRaw({ date: todayKey(), minutesLogged: next, workedOn })
        return next
      })
    },
    [workedOn],
  )

  const resetMinutes = useCallback(() => {
    setMinutesLoggedState(0)
    saveRaw({ date: todayKey(), minutesLogged: 0, workedOn })
  }, [workedOn])

  const setWorkedOn = useCallback((value: string) => {
    setWorkedOnState(value)
    setMinutesLoggedState((prev) => {
      saveRaw({ date: todayKey(), minutesLogged: prev, workedOn: value })
      return prev
    })
  }, [])

  return { minutesLogged, workedOn, addMinutes, resetMinutes, setWorkedOn }
}

export function formatDuration(totalMinutes: number): string {
  const h = Math.floor(totalMinutes / 60)
  const m = totalMinutes % 60
  if (h === 0) return `${m}m`
  if (m === 0) return `${h}h 00m`
  return `${h}h ${String(m).padStart(2, '0')}m`
}
